/*----------------------------------
- DEPENDANCES
----------------------------------*/

// Npm
import Cookie from 'js-cookie';

// Core
import ClientApplication from '@client/app';
import type { CoreError } from '@common/errors';

// Core Services
import Router from '@client/services/router';

// App Services
import MetricsService from '@/client/services/metrics';
import ExtensionBridge from '@/client/services/extension';
import { getGlobalToast } from '@/client/components/ui/Toast';
import { proUnlockFeatureFor } from '@/client/catalogs/proUnlock';
import { requestProUnlock } from '@/client/components/trial/proUnlockEvents';

/*----------------------------------
- TYPES
----------------------------------*/

/** A plan-gate error also names the feature it protects. */
type TUpgradeError = CoreError & { feature?: string };
const asUpgradeError = (error: CoreError): TUpgradeError => error;

/*----------------------------------
- APPLICATION
----------------------------------*/
export class CrossPath extends ClientApplication {
    public Router = new Router(this, {
        preload: [],

        context: (context, router) => ({ user: context.user ?? router.ssrContext?.user ?? null }),
    });

    public metrics = new MetricsService(this);

    public Extension = new ExtensionBridge(this);

    public boot(): void {
        // Set timezone in cookies, so backend can consume it
        Cookie.set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);

        // Tracking
        this.Router.on('page.changed', () => {
            this.metrics.event('pageview');
        });

        // Open pgrade modal
        /*this.Router.on('page.rendered', () => {

            if (Cookie.get('upgraded') === "true") {
                import('@/client/pages/convert/upgrade/UpgradedModal').then(
                    ({ default: UpradeModal }) => this.modal.show(UpradeModal)
                )
            }
        });*/
    }

    /**
     * Routes every app-level error (unhandled promise rejections, plan-gate 402s) through the
     * app's own styled toast system instead of proteum's legacy Dialog manager, which renders an
     * unstyled, bottom-of-page notice — this is what "Upgrade required" looked like before.
     */
    public handleError(error: CoreError) {
        const message = 'render' in error && error.render ? error.render() : error.message;
        const toast = getGlobalToast();

        if (error.http === 402) {
            // Someone on the free trial gets an explanation of what Pro opens; anyone else, or a page with nobody to explain, keeps the message.
            const unlockRequested = requestProUnlock({ feature: proUnlockFeatureFor(asUpgradeError(error).feature), message }, true);
            // Persisted: a plan-gate message is something to act on, not glance at for 4 seconds.
            if (!unlockRequested) toast.warning(message, 'Upgrade required', true);
        } else {
            toast.error(message, undefined, true);
        }
    }

    public handleUpdate(): void {
        window.location.reload();
    }
}

export default CrossPath;
